import React, { useCallback } from 'react';
import { Card, Typography } from 'antd';
import { useModulePermission } from '../../hooks/usePermission';
import './PWM211Form.css';

import { usePWM211Query } from './hooks/usePWM211Query';
import { usePWM211Reply } from './hooks/usePWM211Reply';
import FilterBar from './components/FilterBar';
import ResultGrid from './components/ResultGrid';
import ReplyModal from './components/ReplyModal';
import type { FM030 } from '../../services/pwm211Api';

const { Title } = Typography;

// ====================================================================
// PWM211Form — 庫位狀況回覆作業
// ====================================================================

const PWM211Form: React.FC = () => {
  useModulePermission('pwm211f1');

  const {
    results, total, loading, page, pageSize, filters,
    executeQuery, setFilter, resetFilters,
  } = usePWM211Query();

  const {
    replyOpen, replyRecord, replies, replyLoading, replySaving,
    openReply, closeReply, addReply, removeReply, setReplyField, saveReplies,
  } = usePWM211Reply();

  const handleSearch = useCallback(() => {
    executeQuery(filters, 1);
  }, [executeQuery, filters]);

  const handleReset = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handlePageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      executeQuery(filters, newPage, newPageSize);
    },
    [executeQuery, filters]
  );

  const handleReply = useCallback(
    (row: FM030) => {
      openReply(row);
    },
    [openReply]
  );

  const handleSaveAndClose = useCallback(async () => {
    const ok = await saveReplies();
    if (ok) {
      closeReply();
      // Refresh the current row in results if needed (non-blocking)
    }
  }, [saveReplies, closeReply]);

  return (
    <div className="pwm211-form">
      <div className="pwm211-page-wrapper">
        <Card
          title={<Title level={4} style={{ margin: 0 }}>庫位狀況回覆作業</Title>}
          className="pwm211-card"
        >
          {/* Filter Bar */}
          <FilterBar
            filters={filters}
            loading={loading}
            onSetFilter={setFilter}
            onSearch={handleSearch}
            onReset={handleReset}
          />

          {/* Results Grid */}
          <div className="pwm211-grid-wrapper">
            <ResultGrid
              data={results}
              total={total}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onReply={handleReply}
            />
          </div>
        </Card>
      </div>

      {/* Reply Modal */}
      <ReplyModal
        open={replyOpen}
        record={replyRecord}
        replies={replies}
        loading={replyLoading}
        saving={replySaving}
        onClose={closeReply}
        onAddRow={addReply}
        onRemoveRow={removeReply}
        onSetField={setReplyField}
        onSave={handleSaveAndClose}
      />
    </div>
  );
};

export default PWM211Form;
